import { InlineAlert } from '@ixigo/iui/InlineAlert';
import Typography from '@ixigo/iui/Typography';
import { formatPrice } from '@repo/shared/utils/priceFormatter';
import type { RefundSummary, RefundSummaryMode } from '@/api';
import { LineItem } from '@/components/fare-summary';
import { DynamicIcon, type IconModule } from '@/components/lazy-icons';

const ICON_IMPORT_MAP: Record<RefundSummaryMode, () => Promise<IconModule>> = {
	MONEY: () => import('@ixigo/icons/IxigoMoneyFilled'),
	MONEY_MAX: () => import('@ixigo/icons/IxigoMoneyFilled'),
	ORIGINAL_MODE_PAYMENT: () => import('@ixigo/icons/WalletFilled'),
	OTHERS: () => import('@ixigo/icons/WalletFilled'),
	UPI: () =>
		Promise.resolve({
			default: () => (
				<img
					src="https://images.ixigo.com/image/upload/upi/16e19622f035c990201e7da8cead2b9f-ylncm.png"
					alt="UPI"
					className="h-50px"
				/>
			),
		}),
	NETBANKING: () => import('@ixigo/icons/BankFilled'),
	CARD: () => import('@ixigo/icons/CreditCard'),
};

const ICON_COLOR_MAP: Partial<Record<RefundSummaryMode, string>> = {
	MONEY: 'text-brand',
	MONEY_MAX: 'text-brand',
};

export const TransactionSummary = ({
	refundSummary,
}: {
	refundSummary: RefundSummary;
}) => {
	return (
		<div className="flex flex-col gap-y-5 border border-tertiary rounded-20 p-15 pt-10">
			<Typography variant="body-sm" className="text-secondary">
				{refundSummary.title}
			</Typography>
			<div className="flex flex-col gap-y-5">
				{refundSummary.modes.map((mode) => (
					<div
						key={mode.label}
						className="pb-10 border-b border-tertiary last-of-type:border-none last-of-type:pb-0 flex flex-col gap-y-0.5"
					>
						<LineItem
							label={mode.label}
							value={formatPrice(mode.amount, { currency: 'INR' })}
							leftIcon={
								<DynamicIcon
									iconName={mode.type}
									iconImportMap={ICON_IMPORT_MAP}
									className={ICON_COLOR_MAP?.[mode.type] || 'text-secondary'}
								/>
							}
						/>
						{mode.note && (
							<InlineAlert
								severity="warning"
								variant="moderate"
								textColor="auto"
								title={
									<Typography
										variant="body-sm"
										// biome-ignore lint/security/noDangerouslySetInnerHtml: note is safe html string
										dangerouslySetInnerHTML={{ __html: mode.note }}
									/>
								}
							/>
						)}
					</div>
				))}
			</div>
		</div>
	);
};
